import { c as _c } from "react/compiler-runtime";
import { FormInputNumber, FormSelectInline } from '@systemeio/ui';
import React, { memo, useMemo } from 'react';
import { FieldErrorAndDescription, FieldErrorAndDescriptionProps } from 'shared/components/form/field-error-and-description';
import { FieldLabel, FieldLabelProps } from 'shared/components/form/field-label';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { TimeType, TimeTypeState } from './constants';
import { createTimeTypeOptionsForSelect } from './utils';
export type TimeDelayPickerProps = Omit<React.HTMLProps<HTMLLabelElement>, 'onChange' | 'label' | 'max'> & FieldLabelProps & FieldErrorAndDescriptionProps & {
  selectedTimeValue: number;
  selectedTimeType: TimeType;
  selectedTimeBeforeAfter?: TimeTypeState;
  onChange: (selectedTimeValue: TimeDelayPickerProps['selectedTimeValue'], selectedTimeType: TimeDelayPickerProps['selectedTimeType'], selectedTimeBeforeAfter?: TimeDelayPickerProps['selectedTimeBeforeAfter']) => void;
  classNameInputInteger?: string;
  withTimeAfterBeforeSelect?: boolean;
  isPreFetching?: boolean;
  allowedOptions?: TimeType[];
  max?: number;
};
const TimeDelayPicker = memo(function TimeDelayPicker(t0) {
  const $ = _c(80);
  let allowedOptions;
  let className;
  let classNameInputInteger;
  let description;
  let disabled;
  let error;
  let isPreFetching;
  let label;
  let labelClassName;
  let max;
  let onChange;
  let props;
  let required;
  let selectedTimeBeforeAfter;
  let selectedTimeType;
  let selectedTimeValue;
  let withTimeAfterBeforeSelect;
  if ($[0] !== t0) {
    ({
      description,
      error,
      selectedTimeValue,
      selectedTimeType,
      onChange,
      labelClassName,
      className,
      label,
      required,
      selectedTimeBeforeAfter,
      withTimeAfterBeforeSelect,
      classNameInputInteger,
      isPreFetching,
      allowedOptions,
      disabled,
      max,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = allowedOptions;
    $[2] = className;
    $[3] = classNameInputInteger;
    $[4] = description;
    $[5] = disabled;
    $[6] = error;
    $[7] = isPreFetching;
    $[8] = label;
    $[9] = labelClassName;
    $[10] = max;
    $[11] = onChange;
    $[12] = props;
    $[13] = required;
    $[14] = selectedTimeBeforeAfter;
    $[15] = selectedTimeType;
    $[16] = selectedTimeValue;
    $[17] = withTimeAfterBeforeSelect;
  } else {
    allowedOptions = $[1];
    className = $[2];
    classNameInputInteger = $[3];
    description = $[4];
    disabled = $[5];
    error = $[6];
    isPreFetching = $[7];
    label = $[8];
    labelClassName = $[9];
    max = $[10];
    onChange = $[11];
    props = $[12];
    required = $[13];
    selectedTimeBeforeAfter = $[14];
    selectedTimeType = $[15];
    selectedTimeValue = $[16];
    withTimeAfterBeforeSelect = $[17];
  }
  const {
    t,
    tICU
  } = useLocoTranslation();
  let t1;
  if ($[18] !== t) {
    t1 = t("dashboard.funnel.emails_webinar_start_before");
    $[18] = t;
    $[19] = t1;
  } else {
    t1 = $[19];
  }
  let t2;
  if ($[20] !== t1) {
    t2 = {
      id: TimeTypeState.before,
      caption: t1
    };
    $[20] = t1;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  let t3;
  if ($[22] !== t) {
    t3 = t("dashboard.funnel.emails_webinar_start_after");
    $[22] = t;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  let t4;
  if ($[24] !== t3) {
    t4 = {
      id: TimeTypeState.after,
      caption: t3
    };
    $[24] = t3;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] !== t2 || $[27] !== t4) {
    t5 = [t2, t4];
    $[26] = t2;
    $[27] = t4;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  const timeStates = t5;
  let t6;
  if ($[29] !== allowedOptions || $[30] !== selectedTimeValue || $[31] !== tICU) {
    t6 = createTimeTypeOptionsForSelect(tICU, selectedTimeValue, allowedOptions);
    $[29] = allowedOptions;
    $[30] = selectedTimeValue;
    $[31] = tICU;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  const typeTypeOptions = t6;
  const t7 = `flex flex-col gap-1 text-sm font-medium ${className}`;
  let t8;
  if ($[33] !== label || $[34] !== labelClassName || $[35] !== required) {
    t8 = <FieldLabel label={label} labelClassName={labelClassName} required={required} />;
    $[33] = label;
    $[34] = labelClassName;
    $[35] = required;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== disabled || $[38] !== isPreFetching || $[39] !== onChange || $[40] !== selectedTimeBeforeAfter || $[41] !== selectedTimeType || $[42] !== selectedTimeValue || $[43] !== timeStates || $[44] !== withTimeAfterBeforeSelect) {
    t9 = withTimeAfterBeforeSelect && <FormSelectInline disabled={disabled} withoutCloseIcon={true} data={timeStates} isPreFetching={isPreFetching} value={selectedTimeBeforeAfter} onChange={value => onChange(selectedTimeValue, selectedTimeType, value as TimeTypeState)} />;
    $[37] = disabled;
    $[38] = isPreFetching;
    $[39] = onChange;
    $[40] = selectedTimeBeforeAfter;
    $[41] = selectedTimeType;
    $[42] = selectedTimeValue;
    $[43] = timeStates;
    $[44] = withTimeAfterBeforeSelect;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  const t10 = `w-3/12 ${classNameInputInteger}`;
  let t11;
  if ($[46] !== onChange || $[47] !== selectedTimeBeforeAfter || $[48] !== selectedTimeType) {
    t11 = value_0 => onChange(value_0, selectedTimeType, selectedTimeBeforeAfter);
    $[46] = onChange;
    $[47] = selectedTimeBeforeAfter;
    $[48] = selectedTimeType;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== disabled || $[51] !== isPreFetching || $[52] !== max || $[53] !== selectedTimeValue || $[54] !== t10 || $[55] !== t11) {
    t12 = <FormInputNumber disabled={disabled} className={t10} value={selectedTimeValue} onChange={t11} isPreFetching={isPreFetching} type="integer" max={max} />;
    $[50] = disabled;
    $[51] = isPreFetching;
    $[52] = max;
    $[53] = selectedTimeValue;
    $[54] = t10;
    $[55] = t11;
    $[56] = t12;
  } else {
    t12 = $[56];
  }
  let t13;
  if ($[57] !== onChange || $[58] !== selectedTimeBeforeAfter || $[59] !== selectedTimeValue) {
    t13 = value_1 => onChange(selectedTimeValue, value_1 as TimeType, selectedTimeBeforeAfter);
    $[57] = onChange;
    $[58] = selectedTimeBeforeAfter;
    $[59] = selectedTimeValue;
    $[60] = t13;
  } else {
    t13 = $[60];
  }
  let t14;
  if ($[61] !== disabled || $[62] !== isPreFetching || $[63] !== selectedTimeType || $[64] !== t13 || $[65] !== typeTypeOptions) {
    t14 = <FormSelectInline disabled={disabled} withoutCloseIcon={true} data={typeTypeOptions} value={selectedTimeType} isPreFetching={isPreFetching} onChange={t13} />;
    $[61] = disabled;
    $[62] = isPreFetching;
    $[63] = selectedTimeType;
    $[64] = t13;
    $[65] = typeTypeOptions;
    $[66] = t14;
  } else {
    t14 = $[66];
  }
  let t15;
  if ($[67] !== t12 || $[68] !== t14 || $[69] !== t9) {
    t15 = <div className="flex items-center justify-start gap-4 ">{t9}{t12}{t14}</div>;
    $[67] = t12;
    $[68] = t14;
    $[69] = t9;
    $[70] = t15;
  } else {
    t15 = $[70];
  }
  let t16;
  if ($[71] !== description || $[72] !== error) {
    t16 = <FieldErrorAndDescription description={description} error={error} />;
    $[71] = description;
    $[72] = error;
    $[73] = t16;
  } else {
    t16 = $[73];
  }
  let t17;
  if ($[74] !== props || $[75] !== t15 || $[76] !== t16 || $[77] !== t7 || $[78] !== t8) {
    t17 = <label {...props} className={t7}>{t8}{t15}{t16}</label>;
    $[74] = props;
    $[75] = t15;
    $[76] = t16;
    $[77] = t7;
    $[78] = t8;
    $[79] = t17;
  } else {
    t17 = $[79];
  }
  return t17;
});
export default TimeDelayPicker;